import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Unlink } from "lucide-react";

const REASONS = { scope: "calendar access was not granted — tick the Calendar permission", no_refresh_token: "Google did not give long-term access — try again",
  token: "Google rejected the sign-in", invalid_state: "the sign-in link expired — try again", access_denied: "access was denied" };

function LastSync({ st }) {
  if (!st.last_sync_at) return <span className="text-xs text-slate-400" data-testid="gcal-last-sync">Not synced yet</span>;
  const when = new Date(st.last_sync_at).toLocaleString();
  return st.last_ok
    ? <span className="text-xs text-emerald-700" data-testid="gcal-last-sync">Synced {when}{st.last_result ? ` · ${st.last_result.pushed} sent, ${st.last_result.pulled} received` : ""}</span>
    : <span className="text-xs text-rose-600" data-testid="gcal-last-error">Sync failed {when}: {st.last_error}</span>;
}

export function GoogleCalendarCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/google-calendar/status").then((r) => setSt(r.data)).catch(() => setSt(false));
  useEffect(() => {
    load();
    const g = params.get("gcal");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar connected — syncing appointments now");
    else toast.error(`Google Calendar connection failed: ${REASONS[params.get("reason")] || params.get("reason") || "unknown error"}`);
    params.delete("gcal"); params.delete("reason"); setParams(params, { replace: true });
  }, []); // eslint-disable-line
  const connect = async () => {
    try { const { data } = await api.get("/google-calendar/connect"); window.location.href = data.url; } catch (e) { toast.error(apiErr(e)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Appointments stop syncing (nothing is deleted).")) return;
    try { await api.post("/google-calendar/disconnect"); toast.success("Google Calendar disconnected"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const syncNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/google-calendar/sync"); setSt(data.status); data.ok ? toast.success("Calendar synced") : toast.error(data.error); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!st) return null;
  const redirect = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/calendar/callback`;
  return (
    <Card className="p-5 mb-5" data-testid="gcal-card">
      <h3 className="font-head font-bold text-slate-900">Google Calendar sync — Appointments</h3>
      <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Two-way: CRM appointments appear in Google Calendar (Video ones get a Google Meet link), and busy times in Google block those slots on the public booking page and show in the CRM calendar. Moving a CRM appointment in Google updates it here. Clients are not invited.</p>
      {!st.configured ? (
        <div data-testid="gcal-not-configured" className="mt-3 text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 space-y-1">
          <div className="font-semibold">Not set up yet — Google Client ID and Secret are missing.</div>
          <div className="text-xs">Use the same Google Cloud OAuth client as Sheets sync: enable the Google Calendar API, add this authorized redirect URL, then ask your developer to add the Client ID and Secret securely. Sign in with chahaloverseasimmigration@gmail.com when connecting.</div>
          <code data-testid="gcal-redirect-url" className="block text-xs bg-white border border-amber-200 rounded px-2 py-1 break-all">{redirect}</code>
        </div>
      ) : !st.connected ? (
        <button data-testid="gcal-connect" onClick={connect} className="mt-3 inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold"><CalendarDays className="h-4 w-4" /> Connect Google Calendar</button>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <span data-testid="gcal-connected-as" className="font-semibold text-slate-800">Connected as {st.google_email || "Google account"}</span>
          <LastSync st={st} />
          <div className="ml-auto flex gap-2">
            <button data-testid="gcal-sync-now" disabled={busy} onClick={syncNow} className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 disabled:opacity-60"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Sync now</button>
            <button data-testid="gcal-disconnect" onClick={disconnect} className="inline-flex items-center gap-1.5 border border-rose-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-700"><Unlink className="h-3.5 w-3.5" /> Disconnect</button>
          </div>
        </div>
      )}
    </Card>
  );
}
